import { type Ref, useState } from 'react';
import Image, { type ImageProps } from 'next/image';

import { placeholderImage } from 'graphics';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { round } from 'utils/math';

type ImgSrc = ImageProps['src'];

// Make alt/src required while still being nullable.
// Make height/width fully required since omitting them causes runtime error.
// Use IntrinsicSizeImg for images with optional height/width.
interface Props extends Omit<
	ImageProps,
	'alt' | 'height' | 'src' | 'unoptimized' | 'width'
> {
	alt: string | null | undefined;
	className?: string;
	/** Should a fallback image be displayed if the image fails to load? */
	hasFallbackPlaceholder?: boolean;
	height: `${number}` | number;
	/** If the image should be requested as an optimized JPEG */
	jpgOptimized?: boolean;
	ref?: Ref<HTMLImageElement>;
	src: ImgSrc | null | undefined;
	width: `${number}` | number;
}

const ASPECT_RATIO_CLASSES: Record<number, string> = {
	1: 'aspect-square',
	2: 'aspect-2/1',
	0.75: 'aspect-3/4',
	4: 'aspect-4/1',
	1.33: 'aspect-4/3',
};

export default function Img({
	alt: baseAlt,
	className,
	hasFallbackPlaceholder = true,
	height,
	jpgOptimized = true,
	ref,
	src: baseSrc,
	style,
	width,
	...rest
}: Props) {
	const [imgError, setImgError] = useState(false);

	let src = baseSrc;
	let alt = baseAlt ?? '';

	if ((!src || imgError) && (!hasFallbackPlaceholder || !jpgOptimized)) {
		return null;
	}

	let errorClass = '';
	if (!src || imgError) {
		// The placeholder import is typed as 'any' so undefined doesn't get
		// refined away from src without a cast.
		src = placeholderImage as ImgSrc;
		alt = '';
		// Force aspect ratio since the placeholder image is square.
		const widthNum = Number.parseInt(String(width), 10);
		const heightNum = Number.parseInt(String(height), 10);
		if (widthNum && heightNum) {
			const aspectClass = ASPECT_RATIO_CLASSES[round(widthNum / heightNum, 2)];
			errorClass = aspectClass ? `${aspectClass} object-cover` : '';
		}
	}

	return (
		<Image
			{...rest}
			ref={ref}
			src={src}
			alt={alt}
			aria-hidden={alt === '' ? true : undefined}
			width={width}
			height={height}
			className={cn(className, imgError && errorClass)}
			// Get rid of Next's hardcoded `color: transparent` inline style
			// that hides alt text.
			// https://github.com/vercel/next.js/issues/61388
			style={{ color: undefined, ...style }}
			unoptimized={
				!is.string(src) ||
				(is.string(src) && !src.includes('{imgproxy_params}'))
			}
			loader={(loaderProps) => {
				let loaderSrc = loaderProps.src;
				if (loaderSrc.includes('{imgproxy_params}')) {
					// NOTE! Don't change the params ordering without a good reason, new
					// order leads to new URLs which means new images will be generated.
					const params = [
						jpgOptimized && 'preset:jpgoptimized',
						`w:${loaderProps.width}`,
					].filter(is.truthy);
					loaderSrc = loaderSrc.replace('{imgproxy_params}', params.join('/'));
				}
				return loaderSrc;
			}}
			onError={() => {
				if (hasFallbackPlaceholder) {
					setImgError(true);
				}
			}}
		/>
	);
}
Img.displayName = 'Img';
